import { BodyMetricType } from "../../../enums";

export type BodyMetricGroup = "body" | "composition" | "girths" | "heart";

export interface IBodyMetricTypeConfig {
  unit: string;
  /** Inclusive limits - the API rejects anything outside them with the same numbers. */
  min: number;
  max: number;
  /** Decimals shown and accepted by the form (the API accepts and stores up to 2). */
  decimals: number;
  group: BodyMetricGroup;
}

/**
 * Units, valid ranges and groups of every body metric, in the order the "+" picker and
 * the overview cards show them. Keep min/max in sync with the API validation.
 */
export const BODY_METRIC_TYPES: Record<BodyMetricType, IBodyMetricTypeConfig> = {
  [BodyMetricType.Weight]: { unit: "кг", min: 1, max: 500, decimals: 1, group: "body" },
  [BodyMetricType.Height]: { unit: "см", min: 30, max: 260, decimals: 1, group: "body" },
  [BodyMetricType.BodyFat]: { unit: "%", min: 1, max: 75, decimals: 1, group: "composition" },
  [BodyMetricType.MuscleMass]: { unit: "кг", min: 1, max: 200, decimals: 1, group: "composition" },
  [BodyMetricType.BodyWater]: { unit: "%", min: 20, max: 80, decimals: 1, group: "composition" },
  [BodyMetricType.VisceralFat]: { unit: "ниво", min: 1, max: 60, decimals: 0, group: "composition" },
  [BodyMetricType.BoneMass]: { unit: "кг", min: 0.5, max: 15, decimals: 2, group: "composition" },
  [BodyMetricType.RestingHeartRate]: { unit: "уд/мин", min: 20, max: 250, decimals: 0, group: "heart" },
  [BodyMetricType.Waist]: { unit: "см", min: 20, max: 300, decimals: 1, group: "girths" },
  [BodyMetricType.Chest]: { unit: "см", min: 20, max: 300, decimals: 1, group: "girths" },
  [BodyMetricType.Hips]: { unit: "см", min: 20, max: 300, decimals: 1, group: "girths" },
  [BodyMetricType.Biceps]: { unit: "см", min: 5, max: 150, decimals: 1, group: "girths" },
  [BodyMetricType.Thigh]: { unit: "см", min: 5, max: 150, decimals: 1, group: "girths" },
  [BodyMetricType.Calf]: { unit: "см", min: 5, max: 150, decimals: 1, group: "girths" },
  [BodyMetricType.Neck]: { unit: "см", min: 5, max: 150, decimals: 1, group: "girths" },
  [BodyMetricType.MetabolicAge]: { unit: "г.", min: 12, max: 90, decimals: 0, group: "composition" },
  [BodyMetricType.Vo2Max]: { unit: "мл/кг/мин", min: 5, max: 100, decimals: 1, group: "heart" },
};

export const BODY_METRIC_TYPE_ORDER: BodyMetricType[] = [
  BodyMetricType.Weight,
  BodyMetricType.Height,
  BodyMetricType.BodyFat,
  BodyMetricType.MuscleMass,
  BodyMetricType.BodyWater,
  BodyMetricType.VisceralFat,
  BodyMetricType.BoneMass,
  BodyMetricType.MetabolicAge,
  BodyMetricType.Waist,
  BodyMetricType.Chest,
  BodyMetricType.Hips,
  BodyMetricType.Biceps,
  BodyMetricType.Thigh,
  BodyMetricType.Calf,
  BodyMetricType.Neck,
  BodyMetricType.RestingHeartRate,
  BodyMetricType.Vo2Max,
];

export const BODY_METRIC_GROUPS: { group: BodyMetricGroup; label: string }[] = [
  { group: "body", label: "Тяло" },
  { group: "composition", label: "Състав на тялото" },
  { group: "girths", label: "Обиколки" },
  { group: "heart", label: "Сърце" },
];
